'use client';

import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { isValidPath } from '../utils/path';
import { Role, UserInfo, ROUTE_PERMISSIONS } from '../types/auth';
import { logSecurityWarning } from '../utils/csrf';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  user: UserInfo | null;
  login: () => void;
  logout: () => Promise<void>;
  hasRole: (role: Role | Role[]) => boolean;
  hasPermission: (route: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 環境変数からAPIのベースURLを取得
const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000/api/v1';

// WID132対策: キャッシュのTTLを短縮（機密情報の保存時間を最小化）
const CACHE_TTL_SECONDS = 5; // 5秒に短縮（従来10秒）

// WID132対策: 機密情報を暗号化するための簡易暗号化関数
const encryptData = (data: string): string => {
  // 注意: これは基本的な暗号化で、本格的なセキュリティには不十分
  // 実運用では適切な暗号化ライブラリの使用を推奨
  try {
    return btoa(encodeURIComponent(data));
  } catch {
    return data; // 暗号化失敗時はそのまま返す
  }
};

const decryptData = (encryptedData: string): string => {
  try {
    return decodeURIComponent(atob(encryptedData));
  } catch {
    return encryptedData; // 復号化失敗時はそのまま返す
  }
};

// WID132対策: 機密情報から非機密情報のみを抽出する関数
const sanitizeUserDataForStorage = (userData: any): any => {
  if (!userData) return null;

  // 最小限の非機密情報のみを保存
  return {
    id: userData.id, // ユーザーID（必要最小限）
    role: userData.role, // 役割（アクセス制御に必要）
    // WID132対策: 以下の機密情報は保存しない
    // upn: ログインID（機密情報）
    // email: メールアドレス（個人情報）
    // name: 氏名（個人情報）
    // division: 所属組織（個人情報）
    // position: 役職（個人情報）
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();

  // クライアントサイドでのみ実行されることを保証
  useEffect(() => {
    setIsClient(true);
    // セキュリティ警告をログ出力
    logSecurityWarning();
  }, []);

  // WID132対策: SessionStorageを使用するキャッシュ有効性チェック関数
  const isCacheValid = (): boolean => {
    if (!isClient) return false;

    // WID132対策: LocalStorageからSessionStorageに変更
    const timestamp = sessionStorage.getItem('opam_cache_timestamp');
    if (!timestamp) return false;

    const now = Date.now();
    const cacheTime = parseInt(timestamp, 10);
    const elapsed = (now - cacheTime) / 1000; // 秒に変換

    console.log(`[AuthContext] Cache age: ${elapsed.toFixed(1)}s (TTL: ${CACHE_TTL_SECONDS}s)`);
    return elapsed < CACHE_TTL_SECONDS;
  };

  // WID132対策: SessionStorageに最小限の暗号化データを保存する関数
  const saveToCache = (userData: any) => {
    if (!isClient) return;

    try {
      const timestamp = Date.now().toString();

      // WID132対策: 機密情報を除外した最小限のデータのみ保存
      const sanitizedData = sanitizeUserDataForStorage(userData);

      // WID132対策: LocalStorageからSessionStorageに変更 + 暗号化
      const encryptedData = encryptData(JSON.stringify(sanitizedData));
      const encryptedAuth = encryptData('true');

      sessionStorage.setItem('opam_user_min', encryptedData);
      sessionStorage.setItem('opam_auth_state', encryptedAuth);
      sessionStorage.setItem('opam_cache_timestamp', timestamp);

      console.log('[AuthContext] WID132対策: 最小限の暗号化データをSessionStorageに保存');
    } catch (error) {
      console.error('[AuthContext] Cache save error:', error);
    }
  };

  // WID132対策: キャッシュをクリアする関数を強化
  const clearCache = () => {
    if (!isClient) return;

    // WID132対策: 旧LocalStorageデータも完全削除
    localStorage.removeItem('opam_user');
    localStorage.removeItem('opam_isAuthenticated');
    localStorage.removeItem('opam_cache_timestamp');

    // WID132対策: 新SessionStorageデータを削除
    sessionStorage.removeItem('opam_user_min');
    sessionStorage.removeItem('opam_auth_state');
    sessionStorage.removeItem('opam_cache_timestamp');
    sessionStorage.removeItem('redirectUrl');

    console.log('[AuthContext] WID132対策: LocalStorage・SessionStorage完全クリア');
  };

  // WID214対策: セッションCookieを手動で削除する関数
  const clearSessionCookies = () => {
    if (!isClient) return;

    // セッションCookieを削除
    document.cookie = 'session=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=strict';

    // その他の認証関連Cookieも削除（存在する場合）
    document.cookie = 'auth=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=strict';
    document.cookie = 'token=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=strict';

    console.log('[AuthContext] Session cookies manually cleared');
  };

  // WID132対策: SessionStorageから最小限データを復元する関数
  const restoreFromCache = (): any | null => {
    if (!isClient) return null;

    try {
      // WID132対策: SessionStorageから暗号化データを取得
      const encryptedUser = sessionStorage.getItem('opam_user_min');
      const encryptedAuth = sessionStorage.getItem('opam_auth_state');

      if (!encryptedUser || !encryptedAuth) return null;

      // WID132対策: 復号化
      const userData = JSON.parse(decryptData(encryptedUser));
      const authState = decryptData(encryptedAuth);

      if (authState === 'true' && userData) {
        console.log('[AuthContext] WID132対策: SessionStorageから最小限データを復元');
        return userData;
      }
    } catch (error) {
      console.error('[AuthContext] Cache restore error:', error);
      clearCache(); // エラー時はキャッシュクリア
    }

    return null;
  };

  // 初期化は一度だけ実行（クライアントサイドでのみ）
  useEffect(() => {
    if (!isClient || hasInitialized) return;

    const initialize = async () => {
      console.log('[AuthContext] Initializing...');
      setHasInitialized(true);

      try {
        // キャッシュ無効フラグを管理
        let shouldClearCacheAfterSuccess = false;

        // WID132対策: SessionStorageからの復元を試行
        if (isCacheValid()) {
          const cachedUserData = restoreFromCache();

          if (cachedUserData) {
            // WID132対策: 最小限のデータで一時的に状態復元
            console.log('[AuthContext] WID132対策: 有効なキャッシュから最小限データを復元');

            // 注意: 完全なユーザー情報は含まれていないため、サーバーから再取得が必要
            // ここでは認証状態のみ復元し、すぐにサーバー確認を行う
            setIsAuthenticated(true);
            setIsLoading(false);

            // WID132対策: 完全なユーザー情報はサーバーから再取得
            console.log('[AuthContext] WID132対策: 完全なユーザー情報をサーバーから再取得します');
          }
        } else {
          // キャッシュが無効な場合、後でクリアするフラグを立てる
          console.log('[AuthContext] WID132対策: 期限切れキャッシュを検出（API確認後にクリア予定）');
          shouldClearCacheAfterSuccess = true;
        }

        // 常にサーバーで認証状態を確認（WID132対策: ローカル情報に依存しない）
        console.log('[AuthContext] WID132対策: サーバーで認証状態を確認...');
        console.log('[AuthContext] Cookies:', document.cookie);
        console.log('[AuthContext] API URL:', `${BASE_URL}/whoami`);

        const response = await fetch(`${BASE_URL}/whoami`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          console.log('[AuthContext] Session valid:', userData.name);
          console.log('[AuthContext] User data details:', {
            id: userData.id,
            upn: userData.upn,
            role: userData.role,
            division: userData.division,
            email: userData.email
          });

          // 期限切れキャッシュがあった場合、新しいデータ取得成功後にクリア
          if (shouldClearCacheAfterSuccess) {
            console.log('[AuthContext] WID132対策: 新しい認証情報取得成功、古いキャッシュをクリア');
            clearCache();
          }

          // WID132対策: 完全なユーザー情報をメモリ上のstateに設定
          setUser(userData);
          setIsAuthenticated(true);

          // WID132対策: 最小限の非機密情報のみをSessionStorageに保存
          saveToCache(userData);

          console.log('[AuthContext] User authenticated, leaving redirect to page.tsx');
        } else {
          console.log('[AuthContext] Session invalid');
          console.log('[AuthContext] Response status:', response.status);

          // レスポンスボディも確認
          try {
            const errorText = await response.text();
            console.log('[AuthContext] Response body:', errorText);

            // 401の場合は認証が必要
            if (response.status === 401) {
              console.log('[AuthContext] 401 Unauthorized - clearing any stale data');
              clearCache();
              clearSessionCookies();
            }
          } catch (e) {
            console.log('[AuthContext] Could not read response body:', e);
          }

          setIsAuthenticated(false);
          setUser(null);

          // 失敗した場合はキャッシュをクリア
          clearCache();

          // 未認証でログインページ以外にいる場合はリダイレクト
          const currentPath = window.location.pathname;
          if (currentPath !== '/') {
            const fullPath = window.location.pathname + window.location.search;
            sessionStorage.setItem('redirectUrl', fullPath);
            router.push('/');
          }
        }
      } catch (error) {
        console.error('[AuthContext] Initialization error:', error);
        setIsAuthenticated(false);
        setUser(null);
        clearCache();
        clearSessionCookies();
      } finally {
        console.log('[AuthContext] Initialization complete, setting isLoading=false');
        setIsLoading(false);
      }
    };

    initialize();
  }, [isClient, hasInitialized, router]);

  const login = () => {
    const currentFullPath = window.location.pathname + window.location.search;
    window.location.href = `${BASE_URL}/login${
      currentFullPath !== '/' ? `?next=${encodeURIComponent(currentFullPath)}` : ''
    }`;
  };

  const logout = async () => {
    console.log('[AuthContext] Starting logout process...');
    setIsLoggingOut(true);
    try {
      // WID214対策: ログアウト前に現在のセッション状態をログ出力
      console.log('[AuthContext] Pre-logout cookies:', document.cookie);

      const response = await fetch(`${BASE_URL}/logout`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        console.log('[AuthContext] Server logout successful');

        // WID214 & WID132対策: クライアント側でも確実にセッション情報を削除
        setIsAuthenticated(false);
        setUser(null);

        // WID132対策: LocalStorage・SessionStorage完全クリア
        clearCache();

        // WID214対策: Cookieを手動で削除
        clearSessionCookies();

        console.log('[AuthContext] Post-logout cookies:', document.cookie);
        console.log('[AuthContext] WID132対策: 全クライアントサイドデータ削除完了');

        // ログアウト成功後、ログインページへリダイレクト
        router.push('/');
      } else {
        console.error('[AuthContext] Logout failed:', response.status);

        // WID132 & WID214対策: サーバー側ログアウトが失敗してもクライアント側は確実にクリア
        setIsAuthenticated(false);
        setUser(null);
        clearCache();
        clearSessionCookies();

        try {
          const errorData = await response.json();
          console.error('[AuthContext] Error details:', errorData);
        } catch (jsonError) {
          console.error('[AuthContext] Failed to parse error JSON:', jsonError);
        }

        // エラーでもログインページにリダイレクト
        router.push('/');
      }
    } catch (error) {
      console.error('[AuthContext] Logout failed:', error);

      // WID132 & WID214対策: 例外が発生してもクライアント側は確実にクリア
      setIsAuthenticated(false);
      setUser(null);
      clearCache();
      clearSessionCookies();

      // エラーでもログインページにリダイレクト
      router.push('/');
    } finally {
      setIsLoggingOut(false);
      console.log('[AuthContext] Logout process completed');
    }
  };

  const hasRole = (role: Role | Role[]): boolean => {
    if (!user) return false;
    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(user.role);
  };

  const hasPermission = (route: string): boolean => {
    if (!user) return false;

    // 動的ルートの処理
    const dynamicRoutePattern = route.replace(/\[.*?\]/g, '[id]');
    const permission = ROUTE_PERMISSIONS[dynamicRoutePattern] || ROUTE_PERMISSIONS[route];

    if (!permission) return true; // 権限設定がない場合はアクセス可能

    // ロールチェック
    if (!permission.roles.includes(user.role)) return false;

    // 組織チェック（必要な場合）
    if (permission.divisions) {
      // 事務局・発行関連・システム管理者は組織制限を受けない（全組織アクセス可能）
      const crossOrganizationalRoles: Role[] = ['office', 'issuer_mgr', 'issuer', 'system'];
      if (crossOrganizationalRoles.includes(user.role)) {
        return true;
      }

      // その他のロール（account_mgr、info_mgr）は組織チェック必須
      if (!user.division) return false;
      return permission.divisions.includes(user.division.id);
    }

    return true;
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    isLoggingOut,
    user,
    login,
    logout,
    hasRole,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
